/**
 * SchemaDataQueryPropertyPanel Component
 *
 * Custom property panel for schema_data_query workflow nodes.
 * Provides schema selection, visual filter builder, and advanced JSON query mode.
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertCircle,
  Loader2,
  Plus,
  Trash2,
  ChevronDown,
  ChevronUp,
  RefreshCw,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { ERROR } from '@/styles/tokens';
import { schemaApi, Schema, SchemaField } from '@/services/api/schemaApi';

// Filter operators for simple mode
const FILTER_OPERATORS = [
  { value: 'eq', label: 'Equals' },
  { value: 'ne', label: 'Not Equals' },
  { value: 'gt', label: 'Greater Than' },
  { value: 'gte', label: 'Greater or Equal' },
  { value: 'lt', label: 'Less Than' },
  { value: 'lte', label: 'Less or Equal' },
  { value: 'contains', label: 'Contains' },
  { value: 'in', label: 'In List' },
  { value: 'exists', label: 'Exists' },
];

const isRecord = (value: unknown): value is Record<string, unknown> => (
  typeof value === 'object' && value !== null && !Array.isArray(value)
);

const getErrorMessage = (error: unknown, fallback: string): string => (
  error instanceof Error ? error.message : fallback
);

const isFilterMode = (value: string): value is SchemaDataQueryConfig['filterMode'] => (
  value === 'simple' || value === 'advanced'
);

const isMatchLogic = (value: string): value is NonNullable<SchemaDataQueryConfig['matchLogic']> => (
  value === 'and' || value === 'or'
);

const isSortDirection = (value: string): value is NonNullable<SchemaDataQueryConfig['sortDirection']> => (
  value === 'asc' || value === 'desc'
);

export interface SimpleFilter {
  field: string;
  operator: string;
  value: string;
}

export interface SchemaDataQueryConfig {
  schemaId?: string;
  groupId?: string;
  filterMode: 'simple' | 'advanced';
  filters?: SimpleFilter[];
  matchLogic?: 'and' | 'or';
  rawQuery?: Record<string, unknown>;
  sortField?: string;
  sortDirection?: 'asc' | 'desc';
  limit?: number;
  allFields?: boolean;
  selectedFields?: string[];
  resultVariable?: string;
}

interface SchemaDataQueryPropertyPanelProps {
  node: Node;
  nodes: Node[];
  edges: Edge[];
  onChange: (config: Partial<SchemaDataQueryConfig>) => void;
  className?: string;
}

export const SchemaDataQueryPropertyPanel: React.FC<SchemaDataQueryPropertyPanelProps> = ({
  node,
  onChange,
  className,
}) => {
  // State
  const [schemas, setSchemas] = useState<Schema[]>([]);
  const [schemaFields, setSchemaFields] = useState<SchemaField[]>([]);
  const [loading, setLoading] = useState(false);
  const [schemasLoading, setSchemasLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [rawQueryText, setRawQueryText] = useState('{}');
  const [rawQueryError, setRawQueryError] = useState<string | null>(null);

  // Extract config from node data
  const config = (node.data?.config || {}) as SchemaDataQueryConfig;

  // Load schemas on mount
  useEffect(() => {
    loadSchemas();
  }, []);

  // Load schema fields when schema changes
  useEffect(() => {
    if (config.schemaId) {
      loadSchemaDetails(config.schemaId);
    } else {
      setSchemaFields([]);
    }
  }, [config.schemaId]);

  // Sync rawQuery to text when switching to advanced mode
  useEffect(() => {
    if (config.filterMode === 'advanced') {
      setRawQueryText(JSON.stringify(config.rawQuery || {}, null, 2));
    }
  }, [config.filterMode, config.rawQuery]);

  const loadSchemas = async () => {
    try {
      setSchemasLoading(true);
      const result = await schemaApi.listSchemas({ limit: 100 });
      setSchemas(result.data);
    } catch (error: unknown) {
      setError(getErrorMessage(error, 'Failed to load schemas'));
    } finally {
      setSchemasLoading(false);
    }
  };

  const loadSchemaDetails = async (schemaId: string) => {
    try {
      setLoading(true);
      setError(null);

      const schema = await schemaApi.getSchema(schemaId);
      setSchemaFields(schema.fields || []);
    } catch (error: unknown) {
      setError(getErrorMessage(error, 'Failed to load schema details'));
      setSchemaFields([]);
    } finally {
      setLoading(false);
    }
  };

  // Handlers
  const handleSchemaChange = useCallback(
    (schemaId: string) => {
      onChange({
        schemaId,
        groupId: undefined,
        filters: [],
        selectedFields: [],
      });
    },
    [onChange]
  );

  const handleFilterModeChange = useCallback(
    (mode: 'simple' | 'advanced') => {
      if (mode === 'advanced' && config.filterMode === 'simple') {
        // Convert simple filters to raw query when switching to advanced
        const rawQuery = convertSimpleToAdvanced(config.filters || [], config.matchLogic || 'and');
        const rawText = JSON.stringify(rawQuery, null, 2);
        setRawQueryText(rawText);
        onChange({ filterMode: mode, rawQuery });
      } else {
        onChange({ filterMode: mode });
      }
    },
    [onChange, config.filterMode, config.filters, config.matchLogic]
  );

  const handleAddFilter = useCallback(() => {
    const newFilter: SimpleFilter = { field: '', operator: 'eq', value: '' };
    onChange({ filters: [...(config.filters || []), newFilter] });
  }, [onChange, config.filters]);

  const handleRemoveFilter = useCallback(
    (index: number) => {
      const newFilters = [...(config.filters || [])];
      newFilters.splice(index, 1);
      onChange({ filters: newFilters });
    },
    [onChange, config.filters]
  );

  const handleFilterChange = useCallback(
    (index: number, field: keyof SimpleFilter, value: string) => {
      const newFilters = [...(config.filters || [])];
      newFilters[index] = { ...newFilters[index], [field]: value };
      onChange({ filters: newFilters });
    },
    [onChange, config.filters]
  );

  const handleRawQueryChange = useCallback(
    (value: string) => {
      setRawQueryText(value);
      try {
        const parsed: unknown = JSON.parse(value);
        if (!isRecord(parsed)) {
          setRawQueryError('Query must be a JSON object');
          return;
        }
        onChange({ rawQuery: parsed });
        setRawQueryError(null);
      } catch {
        setRawQueryError('Invalid JSON');
      }
    },
    [onChange]
  );

  const handleFieldToggle = useCallback(
    (fieldName: string, checked: boolean) => {
      const currentFields = config.selectedFields || [];
      const newFields = checked
        ? [...currentFields, fieldName]
        : currentFields.filter((f) => f !== fieldName);
      onChange({ selectedFields: newFields });
    },
    [onChange, config.selectedFields]
  );

  // Helper: Convert simple filters to MongoDB query
  const convertSimpleToAdvanced = (
    filters: SimpleFilter[],
    matchLogic: string
  ): Record<string, unknown> => {
    if (filters.length === 0) return {};

    const conditions = filters
      .filter((f) => f.field)
      .map((f) => {
        const fieldPath = `data.${f.field}`;
        switch (f.operator) {
          case 'eq':
            return { [fieldPath]: f.value };
          case 'ne':
            return { [fieldPath]: { $ne: f.value } };
          case 'gt':
            return { [fieldPath]: { $gt: f.value } };
          case 'gte':
            return { [fieldPath]: { $gte: f.value } };
          case 'lt':
            return { [fieldPath]: { $lt: f.value } };
          case 'lte':
            return { [fieldPath]: { $lte: f.value } };
          case 'contains':
            return { [fieldPath]: { $regex: f.value, $options: 'i' } };
          case 'in':
            return {
              [fieldPath]: {
                $in: f.value.split(',').map((v) => v.trim()),
              },
            };
          case 'exists':
            return { [fieldPath]: { $exists: f.value === 'true' } };
          default:
            return { [fieldPath]: f.value };
        }
      });

    if (conditions.length === 0) return {};
    if (conditions.length === 1) return conditions[0];
    return matchLogic === 'or' ? { $or: conditions } : { $and: conditions };
  };

  // Get selected schema name for display
  const selectedSchema = schemas.find((s) => s.id === config.schemaId);

  return (
    <div className={cn('space-y-4 p-4', className)}>
      {/* Header */}
      <div>
        <h3 className="text-sm font-semibold mb-1">Schema Data Query</h3>
        <p className="text-xs text-muted-foreground">
          Query records from schema group collections
        </p>
      </div>

      {/* Schema Selection */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label htmlFor="schemaId" className="text-sm font-medium">
            Schema <span className="text-red-500">*</span>
          </Label>
          <Button
            size="sm"
            variant="ghost"
            onClick={loadSchemas}
            disabled={schemasLoading}
            className="h-6 px-2"
          >
            <RefreshCw className={cn('h-3 w-3', schemasLoading && 'animate-spin')} />
          </Button>
        </div>
        <Select value={config.schemaId || ''} onValueChange={handleSchemaChange}>
          <SelectTrigger id="schemaId">
            <SelectValue placeholder={schemasLoading ? 'Loading...' : 'Select a schema...'} />
          </SelectTrigger>
          <SelectContent>
            {schemas.map((schema) => (
              <SelectItem key={schema.id} value={schema.id}>
                {schema.displayName || schema.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {selectedSchema && (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="text-xs">
              {schemaFields.length} fields
            </Badge>
            {selectedSchema.currentVersion && (
              <Badge variant="outline" className="text-xs">
                v{selectedSchema.currentVersion}
              </Badge>
            )}
          </div>
        )}
      </div>

      {/* Filter Mode */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Filter Mode</Label>
        <Select
          value={config.filterMode || 'simple'}
          onValueChange={(value) => {
            if (isFilterMode(value)) {
              handleFilterModeChange(value);
            }
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="simple">Simple (Visual Builder)</SelectItem>
            <SelectItem value="advanced">Advanced (JSON Query)</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Simple Mode: Filter Builder */}
      {config.filterMode !== 'advanced' && config.schemaId && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">Filters</Label>
            <Button size="sm" variant="outline" onClick={handleAddFilter} className="h-7">
              <Plus className="h-3 w-3 mr-1" /> Add
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
              <span className="text-sm text-muted-foreground">Loading fields...</span>
            </div>
          ) : (
            <>
              {(config.filters || []).map((filter, index) => (
                <div key={index} className="flex items-center gap-2 p-2 border rounded bg-muted/30">
                  <Select
                    value={filter.field}
                    onValueChange={(v) => handleFilterChange(index, 'field', v)}
                  >
                    <SelectTrigger className="w-28 h-8 text-xs">
                      <SelectValue placeholder="Field" />
                    </SelectTrigger>
                    <SelectContent>
                      {schemaFields.map((f) => (
                        <SelectItem key={f.name} value={f.name}>
                          {f.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filter.operator}
                    onValueChange={(v) => handleFilterChange(index, 'operator', v)}
                  >
                    <SelectTrigger className="w-28 h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {FILTER_OPERATORS.map((op) => (
                        <SelectItem key={op.value} value={op.value}>
                          {op.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Input
                    value={filter.value}
                    onChange={(e) => handleFilterChange(index, 'value', e.target.value)}
                    placeholder="Value or {{var}}"
                    className="flex-1 h-8 text-xs"
                  />

                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => handleRemoveFilter(index)}
                    className="h-8 w-8"
                  >
                    <Trash2 className="h-3 w-3 text-red-500" />
                  </Button>
                </div>
              ))}

              {(config.filters || []).length > 1 && (
                <div className="space-y-2">
                  <Label className="text-xs font-medium">Match Logic</Label>
                  <Select
                    value={config.matchLogic || 'and'}
                    onValueChange={(value) => {
                      if (isMatchLogic(value)) {
                        onChange({ matchLogic: value });
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="and">Match ALL (AND)</SelectItem>
                      <SelectItem value="or">Match ANY (OR)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              {(config.filters || []).length === 0 && (
                <p className="text-xs text-muted-foreground text-center py-4 border rounded border-dashed">
                  No filters. All records will be returned (up to limit).
                </p>
              )}
            </>
          )}
        </div>
      )}

      {/* Advanced Mode: JSON Editor */}
      {config.filterMode === 'advanced' && (
        <div className="space-y-2">
          <Label className="text-sm font-medium">MongoDB Query</Label>
          <Textarea
            value={rawQueryText}
            onChange={(e) => handleRawQueryChange(e.target.value)}
            placeholder='{ "data.deviceId": "{{payload.deviceId}}" }'
            className={cn(
              'font-mono text-xs min-h-[120px]',
              rawQueryError && 'border-red-500'
            )}
          />
          {rawQueryError && (
            <p className="text-xs text-red-500">{rawQueryError}</p>
          )}
          <p className="text-xs text-muted-foreground">
            Use {'{{variable.path}}'} for dynamic values
          </p>
        </div>
      )}

      {/* Sort & Limit (Collapsible) */}
      <div className="space-y-2">
        <button
          type="button"
          className="flex items-center justify-between w-full text-sm font-medium hover:bg-muted/50 rounded p-1 -m-1"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
        >
          <span>Sort & Limit</span>
          {showAdvancedOptions ? (
            <ChevronUp className="h-4 w-4" />
          ) : (
            <ChevronDown className="h-4 w-4" />
          )}
        </button>

        {showAdvancedOptions && (
          <div className="space-y-3 p-3 border rounded bg-muted/20">
            <div className="flex gap-2">
              <div className="flex-1">
                <Label className="text-xs">Sort By</Label>
                <Select
                  value={config.sortField || 'createdAt'}
                  onValueChange={(v) => onChange({ sortField: v })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="createdAt">Created At</SelectItem>
                    <SelectItem value="updatedAt">Updated At</SelectItem>
                    {schemaFields.map((f) => (
                      <SelectItem key={f.name} value={`data.${f.name}`}>
                        {f.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="w-28">
                <Label className="text-xs">Direction</Label>
                <Select
                  value={config.sortDirection || 'desc'}
                  onValueChange={(value) => {
                    if (isSortDirection(value)) {
                      onChange({ sortDirection: value });
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="desc">Descending</SelectItem>
                    <SelectItem value="asc">Ascending</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs">Limit</Label>
              <Input
                type="number"
                value={config.limit || 10}
                onChange={(e) => onChange({ limit: parseInt(e.target.value) || 10 })}
                min={1}
                max={1000}
                className="h-8 text-xs"
              />
              <p className="text-xs text-muted-foreground mt-1">Max: 1000</p>
            </div>
          </div>
        )}
      </div>

      {/* Field Selection */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Checkbox
            id="allFields"
            checked={config.allFields !== false}
            onCheckedChange={(checked) => onChange({ allFields: checked === true })}
          />
          <Label htmlFor="allFields" className="text-sm font-medium cursor-pointer">
            Return All Fields
          </Label>
        </div>

        {config.allFields === false && schemaFields.length > 0 && (
          <div className="border rounded p-2 max-h-32 overflow-y-auto bg-muted/20">
            {schemaFields.map((field) => (
              <div key={field.name} className="flex items-center gap-2 py-1">
                <Checkbox
                  id={`field-${field.name}`}
                  checked={(config.selectedFields || []).includes(field.name)}
                  onCheckedChange={(checked) => handleFieldToggle(field.name, checked === true)}
                />
                <Label htmlFor={`field-${field.name}`} className="text-xs cursor-pointer">
                  {field.name}
                  <span className="text-muted-foreground ml-1">({field.type})</span>
                </Label>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Result Variable */}
      <div className="space-y-2">
        <Label htmlFor="resultVariable" className="text-sm font-medium">
          Store Results In <span className="text-red-500">*</span>
        </Label>
        <Input
          id="resultVariable"
          value={config.resultVariable || 'queryResults'}
          onChange={(e) => onChange({ resultVariable: e.target.value })}
          placeholder="queryResults"
          className="h-8 text-xs"
        />
        <p className="text-xs text-muted-foreground">
          Results as array. Count in <code className="bg-muted px-1 rounded">_{config.resultVariable || 'queryResults'}Count</code>
        </p>
      </div>

      {/* Error Display */}
      {error && (
        <div
          className="flex items-center gap-2 p-2 rounded"
          style={{ backgroundColor: ERROR.bg }}
        >
          <AlertCircle className="h-4 w-4" style={{ color: ERROR.icon }} />
          <span className="text-xs" style={{ color: ERROR.text }}>
            {error}
          </span>
        </div>
      )}
    </div>
  );
};
